import { ChevronLeft, Mail } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { AppFrame } from '../components/frame/AppFrame';
import { UsernameField, type UsernameState } from '../components/UsernameField';
import { Alert, Button, Field, PageSpinner } from '../components/ui';
import { ApiError, changeEmail, changeUsername, type Me } from '../lib/api';
import { authClient, siteUrl } from '../lib/auth-client';
import { useSession } from '../lib/session';
import { CardHead, cardClass } from './Account';

const PROVIDER_NAMES: Record<string, string> = { google: 'Google', discord: 'Discord' };

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });

function UsernameCard({ me }: { me: Me }) {
  const { refresh } = useSession();
  const [value, setValue] = useState(me.username);
  const [state, setState] = useState<UsernameState>('yours');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const locked = Boolean(me.usernameChangeableAt);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (state !== 'free') return;
    setBusy(true);
    setMessage(null);
    try {
      const saved = await changeUsername(value.trim());
      await refresh();
      setMessage({ tone: 'success', text: `Saved. You’re ${saved.username} now.` });
    } catch (failed) {
      setMessage({
        tone: 'error',
        text: failed instanceof Error ? failed.message : 'That didn’t work. Try again.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="username-heading" className={cardClass}>
      <form onSubmit={onSubmit}>
        <div className="space-y-4 p-5 sm:p-6">
          <CardHead id="username-heading" title="Username">
            Your name on posts, comments and leaderboards, and the name games greet you by.
          </CardHead>
          {message && <Alert tone={message.tone}>{message.text}</Alert>}
          {locked && me.usernameChangeableAt ? (
            <p className="rounded-xl bg-field px-4 py-3 text-sm">
              <b className="block">{me.username}</b>
              <span className="text-muted">
                You changed it recently. You can change it again on {day(me.usernameChangeableAt)}.
              </span>
            </p>
          ) : (
            <div className="max-w-[420px]">
              <UsernameField
                value={value}
                onChange={setValue}
                onState={setState}
                label="New username"
                hint="3 to 20 letters, numbers or _. You can change it once every 30 days."
              />
            </div>
          )}
        </div>
        {!locked && (
          <div className="flex justify-end gap-2 rounded-b-[18px] border-t border-divider bg-[#fbfafd] px-5 py-3.5 sm:px-6">
            <Link
              to="/account"
              className="inline-flex min-h-11 items-center rounded-full border border-line bg-card px-5 text-sm font-bold hover:border-muted"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={busy || state !== 'free'}
              className="min-h-11 rounded-full bg-ink px-6 text-sm font-bold text-white hover:bg-ink-soft disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save username'}
            </button>
          </div>
        )}
      </form>
    </section>
  );
}

function EmailCard({ me }: { me: Me }) {
  const hasPassword = me.signInMethods.includes('credential');
  const provider = me.signInMethods.find((method) => PROVIDER_NAMES[method]);
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signInAgain, setSignInAgain] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function send() {
    setBusy(true);
    setError(null);
    setSignInAgain(false);
    try {
      await changeEmail(newEmail.trim(), hasPassword ? password : undefined);
      setSentTo(newEmail.trim());
    } catch (failed) {
      if (failed instanceof ApiError && failed.code === 'sign_in_again') setSignInAgain(true);
      setError(failed instanceof Error ? failed.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void send();
  }

  async function reauthenticate() {
    if (!provider) return;
    await authClient.signIn.social({
      provider: provider as 'google' | 'discord',
      callbackURL: siteUrl('/account/edit#email'),
    });
  }

  return (
    <section id="email" aria-labelledby="email-heading" className={`${cardClass} p-5 sm:p-6`}>
      <div className="space-y-4">
        <CardHead id="email-heading" title="Email">
          Only you and MOGZO see it. You sign in with it, and get links and notices there.
        </CardHead>
        <p className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted">Now:</span>
          <b className="break-all">{me.email}</b>
        </p>

        {sentTo ? (
          <div
            role="status"
            className="space-y-2.5 rounded-[14px] border border-[#f3dfb5] bg-cream px-4 py-3.5"
          >
            <p className="flex items-center gap-2 font-bold break-all">
              <Mail size={18} strokeWidth={2} aria-hidden="true" />
              Check {sentTo}
            </p>
            <p className="text-sm leading-relaxed text-plum">
              We sent a link there. Your email stays {me.email} until you open it, and the link
              works for 24 hours. Once it changes, we’ll tell {me.email} too. Didn’t mean to? Just
              ignore the email.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={send}
                disabled={busy}
                className="min-h-10 rounded-full border border-[#ead19a] bg-card px-4 text-[13px] font-bold disabled:opacity-60"
              >
                Send it again
              </button>
              <button
                type="button"
                onClick={() => {
                  setSentTo(null);
                  setNewEmail('');
                  setPassword('');
                }}
                className="min-h-10 rounded-full px-4 text-[13px] font-bold"
              >
                Use a different email
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="max-w-[420px] space-y-4">
            {error && <Alert>{error}</Alert>}
            {signInAgain && provider && (
              <button
                type="button"
                onClick={reauthenticate}
                className="min-h-11 w-full rounded-full border border-line bg-card px-5 text-sm font-bold hover:border-muted"
              >
                Sign in with {PROVIDER_NAMES[provider]} again
              </button>
            )}
            <Field
              label="New email"
              type="email"
              autoComplete="email"
              required
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
            />
            {hasPassword ? (
              <Field
                label="Your password"
                type="password"
                autoComplete="current-password"
                required
                hint="So nobody else can change it."
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            ) : (
              <p className="text-xs text-muted">
                So nobody else can change it, we may ask you to sign in with{' '}
                {provider ? PROVIDER_NAMES[provider] : 'your account'} again first.
              </p>
            )}
            <Button busy={busy}>Send a confirmation link</Button>
          </form>
        )}
      </div>
    </section>
  );
}

/** mogzo.com/account/edit: change the username (live-checked) and the email (confirmed by link). */
export function EditProfile() {
  const { me } = useSession();
  if (me === undefined) return <PageSpinner />;
  if (me === null) return <Navigate to="/sign-in?next=/account/edit" replace />;
  if (!me.profileComplete) return <Navigate to="/complete-profile" replace />;

  return (
    <AppFrame title="Edit profile">
      <main className="mx-auto flex w-full max-w-[680px] flex-col gap-5 px-4 pt-5 pb-10 sm:px-8 lg:pt-8">
        <Link
          to="/account"
          className="hidden min-h-10 items-center gap-1 self-start rounded-full border border-line bg-card pr-4 pl-2.5 text-sm font-semibold hover:border-muted lg:inline-flex"
        >
          <ChevronLeft size={18} strokeWidth={2.2} aria-hidden="true" />
          Your account
        </Link>
        <h1 className="hidden text-[30px] leading-9 font-bold tracking-[-0.015em] lg:block">
          Edit profile
        </h1>
        <UsernameCard me={me} />
        <EmailCard me={me} />
      </main>
    </AppFrame>
  );
}
